/**
 * Typed functions for every client API endpoint used by the panel (docs/API.md "CLIENT API", TZ 8.14).
 * Paths are relative to /api/v1. Response types come from the generated OpenAPI operations, keyed by the backend
 * operation id, so a path's answer cannot be typed with the wrong schema.
 */
import type {
  AlertPreferencesUpdate,
  ApiKeyCreate,
  ChangePasswordRequest,
  DaypartCompareMode,
  ExportRequest,
  LoginRequest,
  MetricCompareMode,
  PasswordResetConfirmation,
  PasswordResetRequest,
  MetricGrain,
  MetricKey,
  PromoCreate,
  RankingPeriod,
  ReportTemplateCreate,
  ReportTemplateUpdate,
  SeriesFormat,
  StoreTargetSetting,
  StoreUserCreate,
  StoreUserUpdate,
  ProfileUpdate,
  TradingDate,
} from './api_types';
import { createControlEndpoints } from './control_endpoints';
import { instagramAccountHeaders } from './instagram_account_store';
import { createMarketingEndpoints } from './marketing_endpoints';
import type { ApiClient } from './http_client';
import type { AggregateMode } from './metrics_request_plan';
import { withPointSeries } from './metrics_series_format';
import type { operations } from './schema/generated_schema';

/** JSON body of the 200 answer of one backend operation (operation ids are the FastAPI route names). */
type ResponseOf<OperationId extends keyof operations> = operations[OperationId]['responses'][200] extends {
  content: { 'application/json': infer ResponseBody };
}
  ? ResponseBody
  : never;

/** The alerts history asks for the API maximum in one page (center/api_client/api_models.py MAXIMUM_PAGE_SIZE). */
export const ALERTS_PAGE_SIZE = 500;

export type LogoVariant = 'light' | 'dark';
/** Frame of the company logo chosen in the crop dialog (8.12.2). */
export type LogoShape = 'square' | 'circle';

/** "all" or explicit store ids (API: stores=id,id|all). */
export type StoreScope = 'all' | readonly string[];

function storeScopeParameter(storeScope: StoreScope): string | readonly string[] {
  return storeScope === 'all' ? 'all' : storeScope;
}

/** What a screen asks for; grain and format of the wire request are chosen by `useMetricsQuery`. */
export interface MetricsQuery {
  stores: StoreScope;
  from: TradingDate;
  to: TradingDate;
  grain: MetricGrain;
  metrics: readonly MetricKey[];
  compare: MetricCompareMode;
  /** Store series, their aggregate (store_id null) or both (docs/API.md `aggregate`). */
  aggregate: AggregateMode;
  /** Long periods may switch day -> week -> month to stay in the point budget (default: not for hour grain). */
  allowCoarserGrain?: boolean;
}

/** The wire request of `GET /metrics` after planning. */
export interface MetricsRequest extends Omit<MetricsQuery, 'allowCoarserGrain'> {
  format: SeriesFormat;
}

export type { RankingPeriod };

export interface RankingQuery {
  metric: MetricKey;
  period: RankingPeriod;
  from?: TradingDate | undefined;
  to?: TradingDate | undefined;
}

export interface RangeQuery {
  from: TradingDate;
  to: TradingDate;
}

export function createClientEndpoints(apiClient: ApiClient) {
  return {
    auth: {
      login: (loginRequest: LoginRequest) => apiClient.post<ResponseOf<'login'>>('/auth/login', loginRequest),
      logout: () => apiClient.post<ResponseOf<'logout'>>('/auth/logout'),
      me: (signal?: AbortSignal) => apiClient.get<ResponseOf<'get_me'>>('/auth/me', { signal }),
      updateMe: (profileUpdate: ProfileUpdate) =>
        apiClient.patch<ResponseOf<'update_me'>>('/auth/me', profileUpdate),
      changePassword: (changeRequest: ChangePasswordRequest) =>
        apiClient.post<ResponseOf<'change_password'>>('/auth/change-password', changeRequest),
      telegramConnection: (signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_telegram_connection'>>('/auth/telegram', { signal }),
      createTelegramLink: () => apiClient.post<ResponseOf<'create_telegram_link'>>('/auth/telegram/link'),
      deleteTelegramConnection: () =>
        apiClient.delete<ResponseOf<'delete_telegram_connection'>>('/auth/telegram'),
      requestPasswordReset: (resetRequest: PasswordResetRequest) =>
        apiClient.post<ResponseOf<'request_password_reset'>>('/auth/password-reset/request', resetRequest),
      confirmPasswordReset: (confirmation: PasswordResetConfirmation) =>
        apiClient.post<ResponseOf<'confirm_password_reset'>>('/auth/password-reset/confirm', confirmation),
    },
    stores: {
      list: (signal?: AbortSignal) => apiClient.get<ResponseOf<'list_stores'>>('/stores', { signal }),
    },
    /** Monthly visit targets of one store: everyone who sees the store reads them, the company administrator edits. */
    storeTargets: {
      list: (storeId: string, signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'list_store_targets'>>(`/stores/${encodeURIComponent(storeId)}/targets`, {
          signal,
        }),
      save: (storeId: string, targetSetting: StoreTargetSetting) =>
        apiClient.put<ResponseOf<'save_store_target'>>(
          `/stores/${encodeURIComponent(storeId)}/targets`,
          targetSetting,
        ),
      remove: (storeId: string, month: string) =>
        apiClient.delete<ResponseOf<'delete_store_target'>>(
          `/stores/${encodeURIComponent(storeId)}/targets/${encodeURIComponent(month)}`,
        ),
    },
    branding: {
      /** Company administrator only (8.12.2, owner decision 2026-10-01): PNG/JPG <= 2 MB, re-encoded by the API. */
      /** `logoShape` = the square/circle frame chosen in the crop dialog; omitted keeps the current one. */
      uploadLogo: (variant: LogoVariant, logoFile: Blob, logoShape?: LogoShape) => {
        const formData = new FormData();
        formData.append('file', logoFile, 'logo.png');
        return apiClient.postForm<ResponseOf<'upload_company_logo'>>('/branding/logo', formData, {
          query: logoShape === undefined ? { variant } : { variant, shape: logoShape },
        });
      },
      removeLogo: (variant: LogoVariant) =>
        apiClient.delete<ResponseOf<'delete_company_logo'>>('/branding/logo', { query: { variant } }),
      updateColor: (primaryColor: string | null) =>
        apiClient.put<ResponseOf<'update_company_color'>>('/branding/color', { primary_color: primaryColor }),
    },
    analytics: {
      metrics: async (metricsRequest: MetricsRequest, signal?: AbortSignal) =>
        withPointSeries(
          await apiClient.get<ResponseOf<'get_metrics'>>('/metrics', {
            signal,
            query: {
              stores: storeScopeParameter(metricsRequest.stores),
              from: metricsRequest.from,
              to: metricsRequest.to,
              grain: metricsRequest.grain,
              metrics: metricsRequest.metrics,
              compare: metricsRequest.compare,
              aggregate: metricsRequest.aggregate,
              format: metricsRequest.format,
            },
          }),
        ),
      ranking: (rankingQuery: RankingQuery, signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_ranking'>>('/ranking', { signal, query: { ...rankingQuery } }),
      heatmap: (storeId: string, rangeQuery: RangeQuery, signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_heatmap'>>('/heatmap', {
          signal,
          query: { store: storeId, ...rangeQuery },
        }),
      calendarHeatmap: (storeId: string, year: number, signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_calendar_heatmap'>>('/calendar-heatmap', {
          signal,
          query: { store: storeId, year },
        }),
      tradingHours: (storeScope: StoreScope, rangeQuery: RangeQuery, signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_trading_hours'>>('/trading-hours', {
          signal,
          query: { stores: storeScopeParameter(storeScope), ...rangeQuery },
        }),
      dayparts: (
        storeScope: StoreScope,
        date: TradingDate,
        compare: DaypartCompareMode,
        signal?: AbortSignal,
      ) =>
        apiClient.get<ResponseOf<'get_dayparts'>>('/dayparts', {
          signal,
          query: { stores: storeScopeParameter(storeScope), date, compare },
        }),
      hourProfile: (
        storeScope: StoreScope,
        rangeQuery: RangeQuery,
        compare: MetricCompareMode,
        signal?: AbortSignal,
      ) =>
        apiClient.get<ResponseOf<'get_hour_profile'>>('/hour-profile', {
          signal,
          query: { stores: storeScopeParameter(storeScope), ...rangeQuery, compare },
        }),
      /** `withIntervals: false` returns per-day outage counts and minutes only (period overview up to a year). */
      quality: (
        storeScope: StoreScope,
        rangeQuery: RangeQuery,
        withIntervals: boolean,
        signal?: AbortSignal,
      ) =>
        apiClient.get<ResponseOf<'get_quality'>>('/quality', {
          signal,
          query: {
            stores: storeScopeParameter(storeScope),
            ...rangeQuery,
            intervals: withIntervals ? undefined : false,
          },
        }),
      insights: (signal?: AbortSignal) => apiClient.get<ResponseOf<'get_insights'>>('/insights', { signal }),
    },
    alerts: {
      /** One page as large as the API allows: the history shows every visible alert (`unread` counts all). */
      list: (signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'list_alerts'>>('/alerts', { signal, query: { limit: ALERTS_PAGE_SIZE } }),
      markRead: (alertId: string) =>
        apiClient.post<ResponseOf<'read_alert'>>(`/alerts/${encodeURIComponent(alertId)}/read`),
      markAllRead: () => apiClient.post<ResponseOf<'read_all_alerts'>>('/alerts/read-all'),
      preferences: (signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_alert_preferences'>>('/alert-preferences', { signal }),
      savePreferences: (preferencesUpdate: AlertPreferencesUpdate) =>
        apiClient.put<ResponseOf<'put_alert_preferences'>>('/alert-preferences', preferencesUpdate),
      channels: (signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_alert_channels'>>('/alert-channels', { signal }),
    },
    exports: {
      /** The body may also be a store control "control_events" export (shared/export/export_filters.ts). */
      create: (exportRequest: ExportRequest) =>
        apiClient.post<ResponseOf<'create_export'>>('/exports', exportRequest),
      status: (exportId: string, signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'get_export'>>(`/exports/${encodeURIComponent(exportId)}`, { signal }),
      /** The caller's own exports that can still be downloaded (downloaded or expired ones are gone). */
      list: (signal?: AbortSignal) => apiClient.get<ResponseOf<'list_exports'>>('/exports', { signal }),
      remove: (exportId: string) =>
        apiClient.delete<ResponseOf<'delete_export'>>(`/exports/${encodeURIComponent(exportId)}`),
    },
    reportTemplates: {
      list: (signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'list_report_templates'>>('/report-templates', { signal }),
      create: (templateCreate: ReportTemplateCreate) =>
        apiClient.post<ResponseOf<'create_report_template'>>('/report-templates', templateCreate),
      update: (templateId: string, templateUpdate: ReportTemplateUpdate) =>
        apiClient.patch<ResponseOf<'update_report_template'>>(
          `/report-templates/${encodeURIComponent(templateId)}`,
          templateUpdate,
        ),
      remove: (templateId: string) =>
        apiClient.delete<ResponseOf<'delete_report_template'>>(
          `/report-templates/${encodeURIComponent(templateId)}`,
        ),
    },
    calendar: {
      promos: (signal?: AbortSignal) => apiClient.get<ResponseOf<'list_promos'>>('/promos', { signal }),
      createPromo: (promoCreate: PromoCreate) =>
        apiClient.post<ResponseOf<'create_promo'>>('/promos', promoCreate),
      deletePromo: (promoId: string) =>
        apiClient.delete<ResponseOf<'delete_promo'>>(`/promos/${encodeURIComponent(promoId)}`),
      holidays: (year: number, signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'list_holidays'>>('/holidays', { signal, query: { year } }),
    },
    storeUsers: {
      list: (signal?: AbortSignal) =>
        apiClient.get<ResponseOf<'list_store_users'>>('/store-users', { signal }),
      create: (storeUserCreate: StoreUserCreate) =>
        apiClient.post<ResponseOf<'create_store_user'>>('/store-users', storeUserCreate),
      update: (userId: string, storeUserUpdate: StoreUserUpdate) =>
        apiClient.patch<ResponseOf<'update_store_user'>>(
          `/store-users/${encodeURIComponent(userId)}`,
          storeUserUpdate,
        ),
      resetPassword: (userId: string) =>
        apiClient.post<ResponseOf<'reset_store_user_password'>>(
          `/store-users/${encodeURIComponent(userId)}/reset-password`,
        ),
      remove: (userId: string) =>
        apiClient.delete<ResponseOf<'remove_store_user'>>(`/store-users/${encodeURIComponent(userId)}`),
    },
    /** Store control (docs/plans/store_control_v1.md); the section is hidden when the company has none. */
    control: createControlEndpoints(apiClient),
    marketing: createMarketingEndpoints(apiClient.withHeaders(instagramAccountHeaders)),
    apiKeys: {
      list: (signal?: AbortSignal) => apiClient.get<ResponseOf<'list_api_keys'>>('/api-keys', { signal }),
      create: (apiKeyCreate: ApiKeyCreate) =>
        apiClient.post<ResponseOf<'create_api_key'>>('/api-keys', apiKeyCreate),
      revoke: (apiKeyId: string) =>
        apiClient.delete<ResponseOf<'revoke_api_key'>>(`/api-keys/${encodeURIComponent(apiKeyId)}`),
    },
  };
}

export type ClientEndpoints = ReturnType<typeof createClientEndpoints>;

/** Absolute URL of the export download (GET /exports/{id}/download) when the job has no explicit link. */
export function exportDownloadPath(exportId: string): string {
  return `/api/v1/exports/${encodeURIComponent(exportId)}/download`;
}

/** SSE endpoint of the live counter (GET /api/v1/live). */
export const LIVE_STREAM_PATH = '/api/v1/live';
